import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import type { ContentProposal } from '@shared/contentProposal'
import { diffWords } from '@shared/diff/textDiff'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  CONTENT_PROPOSALS_CHANGED,
  listContentProposals,
  updateContentProposal,
} from '../../../services/contentProposals'

export type ApplyProposalAction = (apply: () => Promise<void>, filename: string) => Promise<void>

function ProposalCard({
  item,
  busy,
  onAction,
  onGoTo,
}: {
  item: ContentProposal
  busy: boolean
  onAction: (item: ContentProposal, action: 'edit' | 'accept' | 'reject', markdown?: string) => Promise<boolean>
  onGoTo?: (filename: string, blockId: string, text: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(item.markdown)
  const runs = useMemo(
    () => diffWords(item.currentText, editing ? draft : item.markdown),
    [item.currentText, item.markdown, editing, draft],
  )
  const anchor =
    item.range.type === 'blocks'
      ? item.range.startBlockId
      : item.range.type === 'subtree'
        ? item.range.headingBlockId
        : null
  const pending = item.status === 'pending'
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">
        {item.filename} · {item.source}
      </Typography>
      <Typography variant="caption">
        {new Date(item.createdAt).toLocaleString()} ·{' '}
        {{ pending: 'ממתינה לבדיקה', applying: 'בתהליך החלה', accepted: 'אושרה', rejected: 'נדחתה' }[item.status]}
      </Typography>
      {item.reason && (
        <Typography variant="body2" sx={{ my: 1 }}>
          {item.reason}
        </Typography>
      )}
      <Typography variant="caption">תוספות בירוק · מחיקות באדום ובקו חוצה</Typography>
      <Box
        sx={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          maxHeight: 350,
          overflowY: 'auto',
          my: 1,
          lineHeight: 1.8,
        }}
      >
        {runs.map((run, index) => (
          <Box
            key={index}
            component="span"
            sx={{
              bgcolor:
                run.op === 'insert'
                  ? 'var(--diff-insert-bg)'
                  : run.op === 'delete'
                    ? 'var(--diff-delete-bg)'
                    : undefined,
              textDecoration: run.op === 'delete' ? 'line-through' : undefined,
            }}
          >
            {run.text}
          </Box>
        ))}
      </Box>
      {!!item.decisions?.length && (
        <Box sx={{ my: 1 }}>
          <Typography variant="caption">החלטות על שינויים בודדים</Typography>
          {item.decisions.map((decision, index) => (
            <Typography key={index} variant="body2">
              {decision.action === 'accept' ? 'אושר' : 'נדחה'}: {decision.before || '(תוספת)'} ←{' '}
              {decision.after || '(מחיקה)'}
            </Typography>
          ))}
        </Box>
      )}
      {editing && (
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={16}
          label="הנוסח המוצע"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          disabled={busy}
        />
      )}
      {pending && !item.canApply && (
        <Alert severity="info">ההצעה הוכנה בסביבת תוכן אחרת. יש לפתוח אותה בסשן המקומי שבו הוכנה.</Alert>
      )}
      <Stack direction="row" sx={{ mt: 1, gap: 1, flexWrap: 'wrap' }}>
        {pending &&
          (editing ? (
            <>
              <Button
                disabled={busy}
                onClick={async () => {
                  if (await onAction(item, 'edit', draft)) {
                    setEditing(false)
                  }
                }}
              >
                שמור הצעה
              </Button>
              <Button disabled={busy} onClick={() => setEditing(false)}>
                ביטול עריכה
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                disabled={busy || !item.canApply}
                onClick={() => void onAction(item, 'accept')}
              >
                אשר שינוי
              </Button>
              <Button
                disabled={busy}
                onClick={() => {
                  setDraft(item.markdown)
                  setEditing(true)
                }}
              >
                ערוך הצעה
              </Button>
              <Button disabled={busy} onClick={() => void onAction(item, 'reject')}>
                דחה
              </Button>
            </>
          ))}
        {anchor && onGoTo && <Button onClick={() => onGoTo(item.filename, anchor, '')}>הצג בטקסט</Button>}
      </Stack>
    </Paper>
  )
}

export default function ContentProposalReview({
  bookId,
  onGoTo,
  onApply,
}: {
  bookId: string
  onGoTo?: (filename: string, blockId: string, text: string) => void
  onApply?: ApplyProposalAction
}) {
  const [items, setItems] = useState<ContentProposal[]>([])
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const refresh = useCallback(async () => {
    try {
      setItems(await listContentProposals(bookId))
      setLoaded(true)
      setError('')
    } catch (error) {
      setError(error instanceof Error ? error.message : 'טעינת ההצעות נכשלה')
    }
  }, [bookId])
  useEffect(() => {
    void refresh()
    const onFocus = () => {
      void refresh()
    }
    window.addEventListener('focus', onFocus)
    window.addEventListener(CONTENT_PROPOSALS_CHANGED, onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      window.removeEventListener(CONTENT_PROPOSALS_CHANGED, onFocus)
    }
  }, [refresh])
  const onAction = async (item: ContentProposal, action: 'edit' | 'accept' | 'reject', markdown?: string) => {
    setBusy(true)
    setError('')
    try {
      const execute = async () => {
        const updated = await updateContentProposal(bookId, item, action, markdown)
        setItems((previous) => previous.map((entry) => (entry.id === updated.id ? updated : entry)))
      }
      if (action === 'accept') {
        if (!onApply) {
          throw new Error('לא ניתן להחיל הצעה מחוץ לעורך')
        }
        await onApply(execute, item.filename)
      } else {
        await execute()
      }
      return true
    } catch (error) {
      // Refresh the version after conflicts without hiding the error or discarding the draft.
      try {
        setItems(await listContentProposals(bookId))
      } catch {
        /* Keep the last loaded list. */
      }
      setError(error instanceof Error ? error.message : 'עדכון ההצעה נכשל')
      return false
    } finally {
      setBusy(false)
    }
  }
  const visible = items.filter((item) => showHistory || item.status === 'pending' || item.status === 'applying')
  return (
    <Stack data-testid="proposal-review-list" spacing={1.5} sx={{ p: 1.25, overflowY: 'auto', minHeight: 0 }}>
      <Typography variant="body2">
        ההצעות מופיעות בתוך הטקסט בעורך. כאן אפשר לעבור בין ההצעות ולעיין בהצעות שכבר טופלו.
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button disabled={busy} onClick={() => void refresh()}>
          רענן הצעות
        </Button>
        <Button onClick={() => setShowHistory(!showHistory)}>
          {showHistory ? 'ממתינות בלבד' : 'הצג גם הצעות שטופלו'}
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {!loaded && !error && <Typography>טוען הצעות…</Typography>}
      {loaded && visible.length === 0 && <Typography>אין הצעות ממתינות</Typography>}
      {visible.map((item) => (
        <ProposalCard key={item.id} item={item} busy={busy} onAction={onAction} onGoTo={onGoTo} />
      ))}
    </Stack>
  )
}
